import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TenantConfigContext = createContext(null);

export const useTenantConfig = () => useContext(TenantConfigContext);

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  brand_name: 'Self-Audit Portal',
  logo_url: '',
  primary_color: '#1E426C',
  secondary_color: '#A21D23',
  page_bg: '#EEF1F6',
  surface_color: '#FFFFFF',
  border_color: '#DDE3EC',
  text_primary: '#2F2F2F',
  text_secondary: '#5B6572',
  text_muted: '#8B95A1',
  nav_deep: '#1D2F47',
  font_heading: 'Jost',
  font_body: 'Source Sans 3',
  email_placeholder: 'you@example.com',
  footer_text: 'Quality Assurance Portal',
};

export const TenantConfigProvider = ({ children }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tenant/config`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...DEFAULTS, ...data });
      }
    } catch (e) {
      console.warn('Failed to load tenant config', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  const fetchPublicConfig = useCallback(async (tenantId) => {
    try {
      const res = await fetch(`${API}/api/tenant/config/public/${tenantId}`);
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...DEFAULTS, ...data });
      }
    } catch (e) {
      console.warn('Failed to load public tenant config', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  const fetchDefaultConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/tenant/config/default`);
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...DEFAULTS, ...data });
      }
    } catch (e) {
      console.warn('Failed to load default tenant config', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user && user.tenant_id) {
      fetchConfig();
    } else if (user === false) {
      fetchDefaultConfig();
    }
  }, [user, fetchConfig, fetchDefaultConfig]);

  const updateConfig = useCallback(async (updates) => {
    try {
      const res = await fetch(`${API}/api/tenant/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...DEFAULTS, ...data });
        return data;
      }
    } catch (e) {
      console.error('Failed to update tenant config', e);
    }
    return null;
  }, []);

  return (
    <TenantConfigContext.Provider value={{ config, loaded, fetchConfig, fetchPublicConfig, updateConfig, DEFAULTS }}>
      {children}
    </TenantConfigContext.Provider>
  );
};
